import './settings/settings-leaf.css';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { assistantConnectionsApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { activeLanguage } from '../i18n/index.ts';
import { useSession } from '../state/session.tsx';
import { useAppConfig } from './turnstile.tsx';
import { Alert, Button } from './ui.tsx';
import { planHasFeature } from '@shared/constants/index.ts';
import { formatNumericInstant } from '../i18n/datetime.ts';

/**
 * Settings → ChatGPT & Claude.
 *
 * The whole setup is here: one address to paste into an assistant, the clicks
 * each assistant asks for, a link to the full guide on the docs site, and the
 * assistants already connected with a way to cut each one off. Everything
 * after the paste — discovery, registration, sign-in, the consent screen — is
 * automatic (worker/routes/mcp.ts), which is why there is nothing else to
 * configure on this side.
 *
 * Shown to everybody: each person connects their own assistant, and it works
 * as them, limited to what their role allows. The list is their own
 * connections — an owner's is everyone's, each with whose it is
 * (worker/routes/assistant-connections.ts).
 *
 * The address comes from the server rather than the browser's location: it is
 * the OAuth resource identifier the assistant checks the metadata against, so
 * it has to be the deployment's canonical one even when this page was opened
 * through another hostname.
 */
export default function AiAssistantsSection() {
  const { t } = useTranslation();
  const { company } = useSession();
  const config = useAppConfig();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every paid plan includes it; on Free the consent screen refuses, so this
  // says so first.
  const planGated = company !== null && !planHasFeature(company.effectivePlan, 'ai_assistants');
  const address = config.data?.mcpServerUrl ?? null;
  const guideUrl = assistantGuideUrl(config.data?.helpDocsUrl ?? null);

  const connections = useQuery({
    queryKey: ['assistant-connections'],
    queryFn: () => assistantConnectionsApi.list(),
  });
  const assistants = connections.data?.connections ?? [];
  // Whose each one is matters only on a list that holds somebody else's — an
  // owner's, once a colleague has connected one.
  const showWhose = assistants.some((connection) => !connection.mine);

  const disconnect = useMutation({
    mutationFn: (connectionId: string) => assistantConnectionsApi.disconnect(connectionId),
    onSuccess: () => {
      setError(null);
      void connections.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('apiKeys.disconnectError')),
  });

  async function copyAddress() {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('aiAssistants.copyError'));
    }
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="ai-assistants-heading">
      <div className="work-section-heading">
        <h2 id="ai-assistants-heading" className="mb-3 text-lg font-semibold">
          {t('aiAssistants.heading')}
        </h2>
      </div>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('aiAssistants.description')}</p>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {t('aiAssistants.yourRole')}
        {guideUrl ? (
          <>
            {' '}
            <a href={guideUrl} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
              {t('aiAssistants.guideLink')}
            </a>
          </>
        ) : null}
      </p>

      {planGated ? (
        <div className="mb-4">
          <Alert tone="info" title={t('aiAssistants.upgradeTitle')}>
            <p>{t('aiAssistants.upgradeNote')}</p>
            <a href="#settings-billing" className="mt-2 inline-block font-semibold hover:underline">
              {t('aiAssistants.upgradeCta')}
            </a>
          </Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {planGated || !address ? null : (
        <>
          <div className="mb-5">
            <label htmlFor="ai-assistants-address" className="field-label">
              {t('aiAssistants.addressLabel')}
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="ai-assistants-address"
                className="field-control min-w-0 flex-1 font-mono"
                aria-describedby="ai-assistants-address-hint"
                readOnly
                value={address}
                onFocus={(event) => event.target.select()}
              />
              <Button variant="primary" onClick={() => void copyAddress()}>
                {copied ? t('common.copied') : t('aiAssistants.copy')}
              </Button>
            </div>
            <p className="field-hint" id="ai-assistants-address-hint">
              {t('aiAssistants.addressHint')}
            </p>
          </div>

          <div className="mb-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-md border border-[color:var(--color-line)] p-4">
              <h3 className="mb-2 font-semibold">{t('aiAssistants.claudeHeading')}</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                <li>{t('aiAssistants.claudeStep1')}</li>
                <li>{t('aiAssistants.claudeStep2')}</li>
                <li>{t('aiAssistants.claudeStep3')}</li>
                <li>{t('aiAssistants.claudeStep4')}</li>
              </ol>
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('aiAssistants.claudeTeamNote')}</p>
            </div>
            <div className="rounded-md border border-[color:var(--color-line)] p-4">
              <h3 className="mb-2 font-semibold">{t('aiAssistants.chatgptHeading')}</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                <li>{t('aiAssistants.chatgptStep1')}</li>
                <li>{t('aiAssistants.chatgptStep2')}</li>
                <li>{t('aiAssistants.chatgptStep3')}</li>
                <li>{t('aiAssistants.chatgptStep4')}</li>
                <li>{t('aiAssistants.chatgptStep5')}</li>
              </ol>
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('aiAssistants.chatgptPlanNote')}</p>
            </div>
          </div>
        </>
      )}

      <div className="mb-5 grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 font-semibold">{t('aiAssistants.canHeading')}</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            <li>{t('aiAssistants.can1')}</li>
            <li>{t('aiAssistants.can2')}</li>
            <li>{t('aiAssistants.can3')}</li>
          </ul>
          <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('aiAssistants.cannot')}</p>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">{t('aiAssistants.tryHeading')}</h3>
          <ul className="space-y-1 text-sm italic">
            <li>{t('aiAssistants.try1')}</li>
            <li>{t('aiAssistants.try2')}</li>
            <li>{t('aiAssistants.try3')}</li>
            <li>{t('aiAssistants.try4')}</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[color:var(--color-line)] pt-4">
        <h3 className="mb-2 font-semibold">{t('aiAssistants.connectedHeading')}</h3>
        {/* "None connected" only once the list has actually loaded: said while
            it loads, or after it failed, it would tell somebody an assistant
            they did connect is not there to disconnect. */}
        {connections.isPending ? (
          <p className="text-sm text-[color:var(--color-muted)]">{t('common.loading')}</p>
        ) : connections.isError ? (
          <Alert tone="error">
            <span className="mr-2">{t('aiAssistants.loadError')}</span>
            <Button variant="secondary" onClick={() => void connections.refetch()}>
              {t('aiAssistants.tryAgain')}
            </Button>
          </Alert>
        ) : assistants.length === 0 ? (
          <p className="text-sm text-[color:var(--color-muted)]">{t('aiAssistants.noneConnected')}</p>
        ) : (
          <ul className="divide-y divide-[color:var(--color-line)]">
            {assistants.map((connection) => (
              <li key={connection.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div>
                  <p className="font-medium">{connection.clientName}</p>
                  <p className="text-sm text-[color:var(--color-muted)]">
                    {showWhose
                      ? `${connection.mine ? t('aiAssistants.you') : (connection.userName ?? t('aiAssistants.someoneElse'))} · `
                      : null}
                    {t('apiKeys.connected', { date: formatNumericInstant(connection.createdAt) })}
                    {connection.lastUsedAt
                      ? t('apiKeys.lastUsed', { date: formatNumericInstant(connection.lastUsedAt) })
                      : t('apiKeys.neverUsed')}
                  </p>
                </div>
                <Button
                  variant="danger"
                  busy={disconnect.isPending && disconnect.variables === connection.id}
                  onClick={() => {
                    if (window.confirm(t('apiKeys.confirmDisconnect', { name: connection.clientName }))) {
                      disconnect.mutate(connection.id);
                    }
                  }}
                >
                  {t('apiKeys.disconnect')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * The docs site's guide to connecting ChatGPT and Claude, in the language the
 * app is showing — or null on a deployment with no docs site.
 */
function assistantGuideUrl(docsUrl: string | null): string | null {
  if (!docsUrl) return null;
  return `${docsUrl}${activeLanguage() === 'es' ? '/es' : ''}/docs/chatgpt-and-claude`;
}
